/** MI DOMINO - the table's buttons and gestures that are not drag-and-drop:
 *  side choice (◀ ▶) after tapping a ficha, ¡NO VOY! (pass), undo, La Corneta,
 *  the shake ritual's mouse hands and New match. Offline + online. */
import { NAMES, canPlay } from '../data/config';
import { L, advance, attemptPlace, endHandTranque, fanReact, hideSideChoice, newMatch, nudgeTile, readyToDeal, undoMove } from './engine';
import { airhorn, buzzer, shuffleSound } from './sounds';
import { netSides, sendPlay } from '../net/online';
import { NET, S, pendingTile, setPendingTile, socket } from '../state';
import { banner } from '../ui/banner';
import { $ } from '../ui/dom';
import { log } from '../ui/feed';
import { LANG, t } from '../ui/i18n';

/** Wire the table controls (needs the table DOM: #play-left/right, #btn-paso,
 *  #btn-undo, #btn-corneta, #felt, #new-match). */
export function initControls(): void {
  $('play-left').onclick = () => {
    if (!pendingTile) return;
    hideSideChoice();
    if (NET.active) sendPlay(pendingTile, 'left'); else attemptPlace(pendingTile, 'left');
    setPendingTile(null);
  };
  $('play-right').onclick = () => {
    if (!pendingTile) return;
    hideSideChoice();
    if (NET.active) sendPlay(pendingTile, 'right'); else attemptPlace(pendingTile, 'right');
    setPendingTile(null);
  };

  /* ¡NO VOY! — you declare it yourself, buzzer and all */
  $('btn-paso').onclick = () => {
    if (NET.active) {                       // online: you declare ¡NO VOY! yourself
      const s = NET.snap;
      if (!s || s.turn !== s.mySeat) return;
      buzzer();
      if (s.myHand.some((t: any) => netSides(t).length > 0)) {   // false pass — the table catches you
        setTimeout(() => banner(LANG === 'en' ? 'NO CHEATING — PLAY! 😂' : '¡NO SEAS TRAMPOSO y TIRA! 😂'), 400);
        return;
      }
      socket.emit('pass');
      return;
    }
    if (!S || S.phase !== 'play' || S.turn !== 0 || S.handOver || S.over || S.busy) return;
    buzzer();
    const playable = S.hands[0].filter(canPlay);
    if (playable.length) {
      /* false pass — the table catches you */
      setTimeout(() => {
        banner(LANG === 'en' ? 'NO CHEATING — PLAY! 😂' : '¡NO SEAS TRAMPOSO y TIRA! 😂');
        log(LANG === 'en'
          ? `${NAMES[Math.random() < 0.5 ? 1 : 3]}: No cheating! You DO have a domino... PLAY! 😂`
          : `${NAMES[Math.random() < 0.5 ? 1 : 3]}: ¡NO SEAS TRAMPOSO! Tú sí llevas ficha... ¡TIRA! 😂`, true);
      }, 400);
      return;
    }
    banner(LANG === 'en' ? 'PASS! 🙅' : '¡NO VOY! 🙅');
    log(`${t('you')}: ${LANG === 'en' ? 'PASS!' : '¡NO VOY!'} 😖`);
    S.passes++;
    if (S.passes >= 4) return endHandTranque();
    advance();
  };

  $('btn-undo').onclick = () => undoMove();

  /* 🚩 report the rival team (a partner showing tiles outside the game). Server replays + founder review. */
  $('btn-report').onclick = () => {
    socket.emit('report', (r: any) => log(r && r.ok
      ? L('🚩 Reporte enviado. El equipo de revisión lo verá.', '🚩 Report sent. The review team will check it.')
      : L((r && r.error) || 'No se puede reportar ahora.', (r && r.error) || 'Cannot report right now.'), true));
  };

  /* La Corneta: taunt the whole table (3 per hand) */
  $('btn-corneta').onclick = () => {
    if (!S || (S.cornetas || 0) <= 0) {
      log(L('Ya gastaste tus 3 cornetazos de esta mano. 📯', 'You already used your 3 horn blasts this hand. 📯'), true);
      return;
    }
    S.cornetas--;
    airhorn();
    banner('📯 ¡PRRRAAAA!');
    fanReact('📯');
    log(L(`📯 ¡Cornetazo! (te quedan ${S.cornetas})`, `📯 Horn blast! (${S.cornetas} left)`));
  };

  /* your giant hands follow the mouse while you shake — ¡A MENEAR FICHA! */
  $('felt').addEventListener('mousemove', (ev: any) => {
    if (!S || S.phase !== 'wash') return;
    const fr = $('felt').getBoundingClientRect();
    const x = ev.clientX - fr.left, y = ev.clientY - fr.top;
    const hl = $('wash-hand-l');
    const hr = $('wash-hand-r');
    /* hands glued 1:1 to your mouse — no lag, no easing */
    hl.style.transition = hr.style.transition = 'none';
    const w = Math.sin(Date.now() / 130) * 5;
    hl.style.left = (x - 115) + 'px';
    hl.style.top = (y - 48) + 'px';
    hl.style.transform = `rotate(${-7 + w}deg)`;
    hr.style.left = (x + 16) + 'px';
    hr.style.top = (y - 48) + 'px';
    hr.style.transform = `rotate(${7 - w}deg)`;

    /* your hands PUSH the fichas they touch */
    const board = $('board');
    const br = board.getBoundingClientRect();
    const mx = ev.clientX - br.left, my = ev.clientY - br.top;
    const W = Math.max(br.width - 60, 100), H = Math.max(br.height - 40, 80);
    let pushed = 0;
    for (const el of board.querySelectorAll('.wash-tile')) {
      const ex = (parseFloat(el.style.left) || 0) + 23;
      const ey = (parseFloat(el.style.top) || 0) + 12;
      const dx = ex - mx, dy = ey - my;
      const dist = Math.hypot(dx, dy);
      if (dist < 135) {
        nudgeTile(el, dx / (dist || 1), dy / (dist || 1), W, H);
        pushed++;
      }
    }
    if (pushed) {
      S.washMoves += pushed;
      const now = Date.now();
      if (!(window as any)._lastShf || now - (window as any)._lastShf > 140) {
        shuffleSound();
        (window as any)._lastShf = now;
      }
      if (S.washMoves >= 40) readyToDeal();
    }
  });

  $('new-match').onclick = () => {
    if (NET.active) { log(L('Estás en una mesa online — recarga la página para salir.', 'You are at an online table — reload the page to leave.'), true); return; }
    // the table sits under the dashboard/store pages — close them so the new match shows
    $('dash-page')?.classList.remove('show');
    $('tienda-page')?.classList.remove('show');
    newMatch();
  };
}
